import { useEffect, useState } from "react";
import { Download, PlugZap, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, ts } from "@/lib/api";
import { Panel, Th, Td, btn } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Fld } from "@/components/sap/OrderEditor";
import { IdocRows } from "@/components/sap/IdocPanel";
import { IdocPickupPanel } from "@/components/sap/IdocPickupPanel";

const on = (v) => v === true || String(v).toLowerCase() === "true";
const KEYS = ["sftp_enabled", "sftp_host", "sftp_port", "sftp_user", "sftp_password", "sftp_dir_orders", "sftp_dir_processed", "sftp_dir_error", "sftp_dir_status", "sap_status_enabled", "sftp_dir_idoc", "idoc_file_prefix", "idoc_next_file", "idoc_next_docnum", "idoc_snd_por", "idoc_partner", "idoc_rcv_por"];
const DIRS = [["sftp_dir_orders", "Pickup folder (SAP order files)"], ["sftp_dir_processed", "Processed folder"], ["sftp_dir_error", "Error folder"], ["sftp_dir_status", "Status files to SAP"], ["sftp_dir_idoc", "Order IDoc files to SAP"]];
const IDOC = [["idoc_file_prefix", "IDoc file prefix"], ["idoc_next_file", "Next file number"], ["idoc_next_docnum", "Next IDoc number"], ["idoc_snd_por", "Sender port (SNDPOR)"], ["idoc_partner", "Partner (SNDPRN / RCVPRN)"], ["idoc_rcv_por", "Receiver port (RCVPOR)"]];

const dl = async (f) => {
  const r = await api.get(`/sap/status-files/${f.id}/download`, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = f.filename;
  a.click();
};

const StatusFiles = ({ rows }) => (
  <Panel title="Status files for SAP (LSMW tab-delimited)" testId="sap-status-files">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="border-b border-slate-800"><tr><Th>Created</Th><Th>SAP SO</Th><Th>Amazon PO</Th><Th>Event</Th><Th>Status</Th><Th>Delivery</Th><Th></Th></tr></thead>
        <tbody className="divide-y divide-slate-800/70">
          {rows.map((f) => (
            <tr key={f.id} data-testid={`sap-status-row-${f.id}`}>
              <Td className="whitespace-nowrap font-mono text-slate-500">{ts(f.created_at)}</Td><Td className="font-mono">{f.sales_order}</Td><Td className="font-mono">{f.po_number}</Td>
              <Td className="font-mono text-amber-300">{f.event}</Td><Td className="font-mono text-xs">{f.status_code}</Td>
              <Td><StatusBadge status={f.status === "sent" ? "sent" : f.status === "test_hold" ? "test_hold" : "ready"} label={f.status} />{f.error && <span className="ml-2 text-xs text-rose-300">{f.error}</span>}</Td>
              <Td><Button size="sm" variant="ghost" className={btn.ghost} onClick={() => dl(f)} data-testid={`sap-status-download-${f.id}`}><Download className="h-3.5 w-3.5" /></Button></Td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && <p className="px-5 py-4 text-xs text-slate-500">No status files yet. One is created for every SAP order when its 940 is released / put on hold and when its 945 arrives.</p>}
    </div>
  </Panel>
);

export const SftpPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [orig, setOrig] = useState({});
  const [files, setFiles] = useState([]);
  const [idocs, setIdocs] = useState([]);
  const [layout, setLayout] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => {
    api.get("/settings").then((r) => { setCfg(r.data); setOrig(r.data); });
    api.get("/sap/status-files").then((r) => setFiles(r.data));
    api.get("/sap/idoc-files").then((r) => setIdocs(r.data));
  };
  useEffect(() => { load(); api.get("/sap/status-layout").then((r) => setLayout(r.data)); }, []);
  if (!cfg) return null;
  const set = (k) => (v) => setCfg({ ...cfg, [k]: v });
  const run = async (label, fn) => {
    setBusy(label);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const save = () => run("save", async () => {
    await api.put("/settings", { ...Object.fromEntries(KEYS.filter((k) => !k.startsWith("idoc_next") || String(cfg[k]) !== String(orig[k])).map((k) => [k, cfg[k]])), sftp_port: Number(cfg.sftp_port) || 22 });
    toast.success("SFTP settings saved");
    load();
  });
  const testConn = () => run("test", async () => {
    await save();
    const r = (await api.post("/sap/sftp-test")).data;
    toast.success(`Connected - folders ready (${Object.entries(r.folders).map(([d, n]) => `${d}: ${n}`).join(", ")})`);
  });
  const sync = () => run("sync", async () => {
    const r = (await api.post("/sap/sync")).data;
    r.error ? toast.error(r.error) : toast.success(`${r.files.length} file(s) picked up, ${r.status_sent} status file(s) and ${r.idoc_sent || 0} IDoc(s) sent`);
    load();
  });
  const last = cfg.sftp_last_result || {};
  return (
    <div className="space-y-4">
      <Panel title="SAP SFTP connection" testId="sap-sftp-panel">
        <div className="space-y-4 p-5">
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-3 text-sm text-slate-200"><Switch checked={on(cfg.sftp_enabled)} onCheckedChange={set("sftp_enabled")} data-testid="sftp-enabled" /> SFTP pickup every 5 minutes</label>
            <label className="flex items-center gap-3 text-sm text-slate-200"><Switch checked={on(cfg.sap_status_enabled)} onCheckedChange={set("sap_status_enabled")} data-testid="sap-status-enabled" /> Create SAP status files (940 / 945)</label>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Fld label="Host" value={cfg.sftp_host} onChange={set("sftp_host")} placeholder="sftp.epgroup.co.uk" testId="sftp-host" />
            <Fld label="Port" value={cfg.sftp_port} onChange={set("sftp_port")} testId="sftp-port" />
            <Fld label="Username" value={cfg.sftp_user} onChange={set("sftp_user")} testId="sftp-user" />
            <Fld label={cfg.sftp_password_set ? "Password (saved - leave blank to keep)" : "Password"} value={cfg.sftp_password} onChange={set("sftp_password")} testId="sftp-password" />
            {DIRS.map(([k, l]) => <Fld key={k} label={l} value={cfg[k]} onChange={set(k)} testId={`sftp-${k}`} />)}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            {IDOC.map(([k, l]) => <Fld key={k} label={l} value={cfg[k]} onChange={set(k)} testId={`sftp-${k}`} />)}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={save} disabled={!!busy} className={btn.primary} data-testid="sftp-save-btn"><Save className="mr-1.5 h-4 w-4" /> Save</Button>
            <Button variant="ghost" onClick={testConn} disabled={!!busy} className={btn.ghost} data-testid="sftp-test-btn"><PlugZap className="mr-1.5 h-4 w-4" /> {busy === "test" ? "Testing…" : "Test connection"}</Button>
            <Button variant="ghost" onClick={sync} disabled={!!busy} className={btn.ghost} data-testid="sftp-sync-btn"><RefreshCw className="mr-1.5 h-4 w-4" /> {busy === "sync" ? "Running…" : "Run now"}</Button>
            <span className="text-xs text-slate-500" data-testid="sftp-last-run">{cfg.sftp_last_run ? `Last run ${ts(cfg.sftp_last_run)} — ${last.error || `${(last.files || []).length} file(s), ${last.status_sent || 0} status sent`}` : "Not run yet"}</span>
          </div>
          <p className="text-xs text-slate-500">SAP drops order files (template layout) into the pickup folder. Each file is imported automatically, then moved to Processed (or Error if unreadable) with a timestamp. Errors and duplicates are emailed to the admin and shown under Upload file → Recent imports.</p>
        </div>
      </Panel>
      <StatusFiles rows={files} />
      <IdocPickupPanel />
      <Panel title="SAP order IDoc files (ORDERS05)" testId="sap-idoc-files">
        {idocs.length ? <IdocRows rows={idocs} onChanged={load} showPo /> : <p className="px-5 py-4 text-xs text-slate-500">No IDoc files yet. Switch on SAP IDoc for a customer on Trading Partners - every order received from that customer is then written as rx&lt;number&gt;.&lt;extension&gt;.</p>}
      </Panel>
      {layout && (
        <Panel title="Status file layout (for the SAP LSMW / inbound program)" testId="sap-status-layout">
          <div className="space-y-3 p-5 text-xs text-slate-300">
            <p>Tab-delimited text, CRLF, no header row. One H record per sales order followed by one I record per item. File name EPHUB_STATUS_&lt;SO&gt;_&lt;940|945&gt;_&lt;timestamp&gt;.txt.</p>
            {layout.records.map((r) => <p key={r.type}><span className="font-mono text-amber-300">{r.type}:</span> {r.fields.join(" | ")}</p>)}
            <p>Status codes: {Object.entries(layout.status_codes).map(([k, v]) => `${k} = ${v}`).join("; ")}</p>
          </div>
        </Panel>
      )}
    </div>
  );
};
